import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, interval, of } from 'rxjs';
import { switchMap, filter, take, catchError, map, first } from 'rxjs/operators';
import { ObservabilidadeInitializeService, SDKAnalyticsInstance } from './observabilidade-initialize.service';
import { InitializeQueueService } from './initialize-queue.service';
import { GA4WebStrategy } from '../strategies/ga4-web.strategy';
import { HelperObject } from '../../../helpers/object/object.helper';
import { StoreService } from '../../store/store.service';
import { IServiceResponseUsuario } from '../../../models/services/response-app/response-app.interface';
import { environment } from 'src/environments/environment';

const INTERVALO_POLLING_MS = 1000;
const MAXIMO_TENTATIVAS = 10;

@Injectable({ providedIn: 'root' })
export class GA4InitializeService {
  private observavelInicializacao: Observable<void> | null = null;
  private inicializado = false;
  private readonly readySubject = new ReplaySubject<void>(1);

  constructor(
    private readonly observabilidadeService: ObservabilidadeInitializeService,
    private readonly initializeQueue: InitializeQueueService,
    private readonly storeService: StoreService
  ) {}

  inicializar(): Observable<void> {
    if (this.inicializado) {
      this.notificarPronto();
      return of(undefined);
    }

    if (this.observavelInicializacao) {
      return this.observavelInicializacao;
    }

    this.observavelInicializacao = this.aguardarSDKAnalyticsNoDOM$().pipe(
      switchMap(() => this.storeService.profile.pipe(first())),
      switchMap((usuario) => {
        const sdk = this.observabilidadeService.instance;
        if (!sdk) {
          console.warn('[GA4InitializeService] SDK nao disponivel apos aguardar');
          return of(undefined);
        }

        const idEq3 = this.obterUserId(usuario);
        this.aplicarGlobalUserProperties(sdk, idEq3);
        this.aplicarDefaultParameters(sdk);
        this.inicializado = true;
        this.notificarPronto();
        console.info('[GA4InitializeService] GA4 inicializado com sucesso', { idEq3 });
        return of(undefined);
      }),
      catchError((error) => {
        console.error('[GA4InitializeService] Falha ao inicializar GA4', error);
        return of(undefined);
      })
    );

    return this.observavelInicializacao;
  }

  onReady$(): Observable<void> {
    return this.readySubject.asObservable();
  }

  get instance(): SDKAnalyticsInstance | undefined {
    return this.observabilidadeService.instance;
  }

  get estaInicializado(): boolean {
    return this.inicializado && this.observabilidadeService.instance !== undefined;
  }

  private obterUserId(usuario: IServiceResponseUsuario): string {
    return usuario.ideq_cnpj_principal?.toLowerCase() || 'unknown';
  }

  private aplicarGlobalUserProperties(sdk: SDKAnalyticsInstance, idEq3: string): void {
    if (!sdk.setUserProperties) return;

    const globalProps = {
      analytics: {
        user: {
          user_id: idEq3,
          customer_segment: 'pj'
        },
      }
    };

    const snakeCaseProps = HelperObject.deepCamelToSnake(globalProps as Record<string, unknown>);
    sdk.setUserProperties(snakeCaseProps);
  }

  private aplicarDefaultParameters(_sdk: SDKAnalyticsInstance): void {
    const values: GA4DefaultParametersValues = {
      business: environment.ga4Business,
      business_channel: environment.ga4BusinessChannel,
      environment: 'logado'
    };

    const strategy = new GA4WebStrategy();
    strategy.aplicarDefaultParameters(values, environment.gtm);
  }

  private notificarPronto(): void {
    this.readySubject.next();
    this.initializeQueue.notificarPronto('ga4');
  }

  private sdkProntoNoDOM(): boolean {
    return typeof window !== 'undefined' && !!window.SDKAnalytics?.Web;
  }

  private aguardarSDKAnalyticsNoDOM$(): Observable<void> {
    if (this.sdkProntoNoDOM()) {
      return of(undefined);
    }

    return interval(INTERVALO_POLLING_MS).pipe(
      map((i) => i + 1),
      filter((tentativa) => this.sdkProntoNoDOM() || tentativa >= MAXIMO_TENTATIVAS),
      take(1),
      map(() => undefined)
    );
  }
}
